import { ApolloError } from '@apollo/client';
import { Box, Center } from '@chakra-ui/react';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Outlet,
  useHref,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';
import { SortingRule } from 'react-table';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers/AuthProvider';
import { useDetectionContext } from '../../context';
import {
  CrowdStrikeConfiguration,
  EDRConfiguration,
  useGetEDRConfigurationPublic,
} from '../../../entities/queries';
import {
  IGetAlertMetadata,
  IGetDetections,
  IQueryAlertsPaginated,
  useGetAlertMetadata,
  useGetDetections,
  useQueryDetectionsPaginated,
} from '../../queries';
import { Alert } from '@investigator/shared/generated/graphql';

import { DetectionQueryVariables } from '..';
import { DetectionDetailsHeader } from '../../components';

interface IDetectionId {
  detection_id: string;
  tenant: string;
}

interface IGetDetectionVariables {
  detection_ids: IDetectionId[];
}

export type DetectionAlertsFilters = { id: string; value: string }[];

export interface IDetectionContext {
  detectionData: IGetDetections;
  detectionError: ApolloError;
  detectionLoading: boolean;
  getDetectionVariables: IGetDetectionVariables;
  handleFetchRelated: () => void;
  alertsSize: number;
  alertsOffset: number;
  alertsSortBy: SortingRule<Alert>[];
  alertsPageSize: number;
  alertsPageCount: number;
  alertsPageIndex: number;
  onFetchAlertsData: () => void;
  handleColFilters: () => void;
  handleFetchRelatedAlerts: () => void;
  alertsLoading: boolean;
  alertsError: ApolloError;
  alertsData: IQueryAlertsPaginated;
  metadataLoading: boolean;
  metadataData: IGetAlertMetadata;
  metadataError: ApolloError;
  edrConfig: CrowdStrikeConfiguration;
  edrConfigLoading: boolean;
  edrConfigError: ApolloError;
  fetchRelatedDetectionsVariables: DetectionQueryVariables;
  setFetchRelatedLoading: Dispatch<SetStateAction<boolean>>;
  fetchOriginalRelatedCount: number;
  setFetchRelatedCount: Dispatch<SetStateAction<number | undefined>>;
}

export const Detection = () => {
  const location = useLocation();
  const { search } = location;

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const [searchParams] = useSearchParams();

  const { detection_id } = useParams();

  const { start, end } = useDateRangeQueryString();

  const { user } = useContext(AuthContext);

  const { setDetectionData } = useDetectionContext();

  const { backToTriage } = i18n.pages.detections;

  const { detections } = ROUTES;

  const getDetectionVariables = {
    detection_ids: [
      {
        detection_id,
        tenant: user?.attributes?.['custom:tenant_id'],
      },
    ],
  };

  const {
    loading: detectionLoading,
    data: detectionData,
    error: detectionError,
    refetch,
  } = useGetDetections({
    skip: !detection_id,
    variables: getDetectionVariables,
  });

  // Handle refetch_main search param from child components
  // Note: This param is primarily for the Detections list page, but we also
  // refetch the single detection here to keep it in sync
  useEffect(() => {
    const refetch_main = searchParams.get('refetch_main');
    if (refetch_main) {
      refetch();
      // DO NOT remove the param - it needs to persist so the Detections list page
      // can see it when the user navigates back
    }
  }, [searchParams, refetch]);

  const detectionsData = detectionData?.getDetections?.detections;

  const detection = detectionsData?.[0];

  // Update DetectionContext with the current detection data
  useEffect(() => {
    if (detection) {
      setDetectionData(detection);
    }
  }, [detection, setDetectionData]);

  const {
    loading: metadataLoading,
    data: metadataData,
    error: metadataError,
  } = useGetAlertMetadata({
    skip:
      !detection?.tenant_info?.tenant_id ||
      !detectionsData?.[0]?.alert_info?.content_id,
    variables: {
      items: [
        {
          content_id: detectionsData?.[0]?.alert_info?.content_id,
          tenant: detection?.tenant_info?.tenant_id,
        },
      ],
    },
  });

  /* Related / Detections / Entities */

  const [, setFetchRelatedCount] = useState<number>(0); // TODO: looks to be unused, once verified that we don't need, remove
  const [fetchOriginalRelatedCount, setFetchOriginalRelatedCount] =
    useState<number>(0);
  const [, setFetchRelatedLoading] = useState<boolean>(false); // TODO: looks to be unused, once verified that we don't need, remove

  const relatedQuery = useMemo(
    () =>
      `{"query":{"bool":{"must":[{"range":{"total_alert_count":{"gt":0}}},{"range":{"latest_start_timestamp":{"gte":${start}}}}${
        detection?.tenant_info?.tenant_id ? ',' : ''
      }${
        detection?.tenant_info?.tenant_id
          ? `{"terms":{"tenant":["${detection?.tenant_info?.tenant_id}"]}}`
          : ''
      },{"range":{"earliest_start_timestamp":{"lte":${end}}}},{"bool":{"should":[{"term":{"detection_keys.tenant_detection":"${
        detectionsData?.[0]?.detection_keys?.tenant_detection
      }"}},{"term":{"detection_keys.tenant_entity":"${
        detectionsData?.[0]?.detection_keys?.tenant_entity
      }"}}]}}],"must_not":{"term":{"detection_id":"${detection_id}"}}}}}`,
    [
      end,
      start,
      detection_id,
      detectionsData,
      detection?.tenant_info?.tenant_id,
    ],
  );

  const [fetchData, { loading }] = useQueryDetectionsPaginated({
    variables: {
      query: relatedQuery,
    },
    onCompleted: (data) => {
      setFetchRelatedCount(data?.queryDetectionsPaginated?.total_items);
      setFetchOriginalRelatedCount(data?.queryDetectionsPaginated?.total_items);
    },
  });

  useEffect(() => {
    if (
      end &&
      start &&
      detection?.tenant_info?.tenant_id &&
      detectionsData?.[0]?.detection_keys
    ) {
      fetchData();
    }
  }, [
    end,
    start,
    fetchData,
    detectionsData,
    detection?.tenant_info?.tenant_id,
  ]);

  useEffect(() => {
    setFetchRelatedLoading(loading);
  }, [loading]);

  const triagePageParams = useMemo(() => {
    detection_id && params.set('detection_id', detection_id);
    return params.toString();
  }, [detection_id, params]);

  const triagePageHref = useHref({
    pathname: detections,
    search: triagePageParams,
  });

  /* EDR Configuration */

  const {
    loading: edrConfigLoading,
    error: edrConfigError,
    data: edrConfigData,
  } = useGetEDRConfigurationPublic({
    skip: !detection_id && !detection?.tenant_info?.tenant_id,
    variables: {
      provider_name: 'crowdstrike',
      tenant: detection?.tenant_info?.tenant_id ?? undefined,
    },
  });

  const edrConfig = edrConfigData?.getEDRConfiguration as EDRConfiguration;

  useEffect(() => {
    return () => {
      // Reset detection-related state on unmount or navigation
      setFetchRelatedCount(0);
      setFetchOriginalRelatedCount(0);
      setFetchRelatedLoading(false);
    };
  }, []);

  if (!detection_id) {
    return null;
  }

  return (
    <Box data-testid="detection-wrapper">
      {detectionLoading || detectionError ? (
        <Center w="100%">
          <GraphQLReqStatus loading={detectionLoading} error={detectionError} />
        </Center>
      ) : (
        <DetectionDetailsHeader
          getDetectionVariables={getDetectionVariables}
          prevPage={triagePageHref}
          breadcrumbs={[{ link: triagePageHref, label: backToTriage }]}
          metadataLoading={metadataLoading}
        />
      )}
      <Box>
        <Outlet
          context={{
            detectionData,
            detectionError,
            detectionLoading,
            getDetectionVariables,
            metadataLoading,
            metadataData,
            metadataError,
            edrConfig,
            edrConfigError,
            edrConfigLoading,
            fetchOriginalRelatedCount,
            setFetchRelatedCount,
            setFetchRelatedLoading,
          }}
        />
      </Box>
    </Box>
  );
};
